// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState } from 'react';

import type { MachineElement } from '@evolvemep/api-ts';
import {
  ActionIcon,
  Box,
  Card,
  Flex,
  getDefaultZIndex,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure, useHorizontalCollapse } from '@mantine/hooks';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AutodeskExtensionStoreProvider } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNotNil } from 'helpers/isNotNil';
import { BendModelBrowser } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendModelBrowser';
import { BendModelBrowserControls } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendModelBrowserControls';
import { betaFeatureText } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreview';
import {
  BendQuickView,
  type BendQuickViewProps,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendQuickView';
import { isNotNilOrEmptyString } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { useBendModelBrowserState } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendModelBrowserState';
import { ModelSelect } from 'modules/Shop/ModelBrowser/ModelSelect';
import { useModelSelection } from 'modules/Shop/ModelBrowser/useModelSelection';

type Props = {
  element: MachineElement;
} & BendQuickViewProps;

type PreviewType = 'quickView' | 'modelBrowser';
const previewTypeOptions: { label: string; icon: EvolveIconName; value: PreviewType }[] = [
  { label: 'Quick View', icon: 'QuickView', value: 'quickView' },
  { label: 'Model Browser', icon: 'ModelBrowser', value: 'modelBrowser' },
];

/** Expandable drawer. Must be positioned inside a relatively positioned container. */
const ControlsDrawer = ({ children }: { children: ReactNode }) => {
  const [expanded, { toggle }] = useDisclosure(true);
  // By default, `Collapse` sets display to `none`. But that messes up the height of the adjacent ActionIcon.
  // So we instead use the hook version and extract the `display` value from the style.
  const { getCollapseProps } = useHorizontalCollapse({ expanded });
  const {
    style: { display: _display, ...collapseStyle },
    ...collapseProps
  } = getCollapseProps();
  return (
    <Flex pos="absolute" top={8} left={0} style={{ zIndex: getDefaultZIndex('app') + 1 }} p={0}>
      <Box style={collapseStyle} {...collapseProps}>
        <Card
          display="block"
          bg="gray.0"
          ml={8}
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, width: 'max-content' }}
        >
          {children}
        </Card>
      </Box>
      <ActionIcon
        onClick={toggle}
        variant="filled"
        aria-label={expanded ? 'Collapse preview controls' : 'Expand preview controls'}
        color="gray.2"
        h="auto"
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >
        <EvolveIcon
          color="black"
          icon="NavCollapseCollapsed"
          spanProps={{
            style: { transform: `rotate(${expanded ? '180' : '0'}deg)`, transition: 'transform 0.2s ease-in-out' },
          }}
        />
      </ActionIcon>
    </Flex>
  );
};

/**
 * Renders a view that allows the user to pick between quick view and model preview,
 * for MachineElements that have a spool and/or sourceIds specified
 */
export const QuickViewAndModelPreview = ({ element, bendType, ...rest }: Props) => {
  const [previewType, setPreviewType] = useState<PreviewType>('quickView');
  const bendModelBrowserState = useBendModelBrowserState(element);
  const { selectedModel, modelSelectProps } = useModelSelection(element.projectId, {
    spool: isNotNilOrEmptyString(element.spool) ? element.spool : null,
  });

  return (
    <Stack miw="100%" mih="100%" gap={0}>
      <Flex justify="space-between" mb="xs">
        <Stack gap={0}>
          <Title order={5}>{bendType}</Title>
          {previewType === 'modelBrowser' ? (
            <Group>
              <ModelSelect {...modelSelectProps} />
            </Group>
          ) : (
            betaFeatureText
          )}
        </Stack>
        <Box pos="relative">
          <SegmentedControl
            value={previewType}
            onChange={setPreviewType}
            data={previewTypeOptions.map(({ label, icon, value: optionValue }) => ({
              label: (
                <Stack key={optionValue} align="center" gap="xs" miw={100}>
                  <EvolveIcon icon={icon} />
                  <Text>{label}</Text>
                </Stack>
              ),
              value: optionValue,
            }))}
          />
        </Box>
      </Flex>
      <Stack flex={1} gap={0} pos="relative">
        {previewType === 'quickView' ? (
          <BendQuickView bendType={bendType} {...rest} />
        ) : isNotNil(selectedModel) ? (
          <>
            <ControlsDrawer>
              <BendModelBrowserControls element={element} {...bendModelBrowserState} />
            </ControlsDrawer>
            <AutodeskExtensionStoreProvider storeName="apsViewer">
              <AutodeskExtensionStoreProvider storeName="spools">
                <BendModelBrowser element={element} model={selectedModel} previewState={bendModelBrowserState.state} />
              </AutodeskExtensionStoreProvider>
            </AutodeskExtensionStoreProvider>
          </>
        ) : (
          <Flex c="dimmed" h="100%" justify="center" align="center">
            <Text>Select a model to view it here.</Text>
          </Flex>
        )}
      </Stack>
    </Stack>
  );
};
